import { Box, Grid, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Fragment, useState } from 'react';
import LineTaxCell from '../../../common/Tax/LineTaxCell';
import { useGetShopTaxes } from '../../../services/core/settings';
import { INVENTORY_MANAGE_TYPE, INVENTORY_TYPES, PART_TYPES, PRICING_TYPES } from '../../../utility/Constant';
import { NumericEngine } from '../../../utility/helpers/numericEngine';
import { shouldShowPerServiceSubtotalRow } from '../../../utility/helpers/shouldShowPerServiceSubtotalRow';
import { DOCUMENT_TAX_TYPE, LINE_TAX_KIND, isNewTaxVersion, recordPartGrossAmount } from '../../../utility/helpers/taxCalculation';
import AddInventoryModal from '../../Inventory/AddInventory';
import PartBrandSvg from '../../../utility/Svg/PartBrandSvg';
import PartCategorySvg from '../../../utility/Svg/PartCategorySvg';

const currency = (value) => NumericEngine.format(value || 0, { formatCurrency: true, trailingZeros: true });

/** Per-service subtotal, same rule as the invoice detail page. */
const serviceSubtotal = (service) => {
    if (!service) return '0';

    if (service.pricingType === PRICING_TYPES.FXIED) {
        return service.fixedPrice || '0';
    }

    if (service.pricingType === PRICING_TYPES.LABOUR) {
        let subtotal = service?.labor?.totalAmount || '0';

        (service?.parts || []).forEach((part) => {
            // qty × (cost + per-unit fee) — totalAmount is qty × cost only (see recordPartGrossAmount).
            subtotal = NumericEngine.add(String(subtotal), String(recordPartGrossAmount(part)));
        });

        return subtotal;
    }

    return '0';
};

/**
 * One service block on the work order detail page — the blue header plus its
 * labour / parts / fixed-price rows. Markup and spacing mirror the invoice detail
 * service table so the two documents read identically.
 *
 * Work orders carry their own tax rate key (`workOrderAuthorizedRate`), so the line tax cells use
 * DOCUMENT_TAX_TYPE.WORK_ORDER — see docs/work-orders/WORK_ORDERS_SPEC.md §8.3.
 */
const WorkOrderServiceTable = ({ item, workOrderData, provinceTaxGroup, documentHasPartsOrPackages }) => {
    const { data: shopTaxesData } = useGetShopTaxes();
    const showLineTax = isNewTaxVersion(workOrderData);

    // Clicking a managed part opens its inventory record read-only, same as the invoice detail page.
    const [selectedInventoryId, setSelectedInventoryId] = useState(null);
    const [batchNumber, setBatchNumber] = useState(null);
    const [openPartModel, setOpenpartModel] = useState(false);
    const [inventoryOption, setInventoryOption] = useState(null);

    const closePartModal = () => {
        setOpenpartModel(false);
        setInventoryOption(null);
        setSelectedInventoryId(null);
        setBatchNumber(null);
    };

    const subtotal = serviceSubtotal(item) ?? '0';

    return (
        <>
            <Grid
                container
                item
                sx={{
                    bgcolor: 'secondary.main',
                    color: 'white',
                    mt: '32px',
                    borderTopLeftRadius: '4px',
                    borderTopRightRadius: '4px',
                }}
            >
                <Grid item xs={12}>
                    <Typography sx={{ p: '16px', fontSize: '20px', fontWeight: 500, lineHeight: '32px', letterSpacing: '0.15px' }}>
                        {item?.serviceName || item?.serviceId?.name || item?.service?.name}
                    </Typography>
                </Grid>
            </Grid>

            <Grid container item>
                <Grid item xl={12} md={12} sm={12} xs={12}>
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table
                            aria-label="work order service table"
                            sx={{
                                width: '100%',
                                tableLayout: 'fixed',
                                '& .MuiTableCell-root': {
                                    borderBottom: '1px solid rgba(0,0,0,0.12)',
                                    verticalAlign: 'middle',
                                    color: '#212121',
                                },
                            }}
                        >
                            <colgroup>
                                <col style={{ width: 56 }} />
                                <col />
                                <col style={{ width: 96 }} />
                                <col style={{ width: 90 }} />
                                <col style={{ width: 100 }} />
                                {showLineTax && <col style={{ width: 52 }} />}
                            </colgroup>

                            <TableHead sx={{ backgroundColor: '#F8F8F8', height: '40px', borderBottom: 'none' }}>
                                <TableRow sx={{ height: '40px', borderBottom: 'none', '& > th, & > td': { px: '12px' } }}>
                                    <TableCell
                                        sx={{ width: '56px', fontSize: '14px', borderBottom: 'none' }}
                                        style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: '16px', paddingRight: 0 }}
                                    >
                                        No.
                                    </TableCell>
                                    <TableCell
                                        sx={{ fontSize: '14px', paddingTop: 0, paddingBottom: 0, borderBottom: 'none' }}
                                        style={{ height: '40px', paddingLeft: 0, paddingRight: '10px' }}
                                    >
                                        Description
                                    </TableCell>
                                    <TableCell sx={{ width: '96px', fontSize: '14px', borderBottom: 'none' }} style={{ padding: '0 10px' }}>
                                        Quantity
                                    </TableCell>
                                    <TableCell sx={{ width: '90px', fontSize: '14px', borderBottom: 'none' }} style={{ padding: '0 10px' }}>
                                        Price
                                    </TableCell>
                                    <TableCell
                                        align={showLineTax ? 'left' : 'right'}
                                        sx={{ width: '100px', fontSize: '14px', borderBottom: 'none' }}
                                        style={{ padding: '0 10px' }}
                                    >
                                        Amount
                                    </TableCell>
                                    {showLineTax && (
                                        <TableCell
                                            align="center"
                                            sx={{ width: '52px', fontSize: '14px', borderBottom: 'none' }}
                                            style={{ padding: '0 16px 0 10px' }}
                                        >
                                            Tax
                                        </TableCell>
                                    )}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {/* Optional service note */}
                                {item?.description && (
                                    <TableRow>
                                        <TableCell style={{ padding: '6px 0 6px 16px' }} />
                                        <TableCell style={{ padding: '6px 10px 6px 0' }}>
                                            <Typography variant="body2" sx={{ fontSize: '16px', fontWeight: 400, lineHeight: '143%' }}>
                                                {item?.description}
                                            </Typography>
                                        </TableCell>
                                        <TableCell style={{ padding: '6px 10px' }} />
                                        <TableCell style={{ padding: '6px 10px' }} />
                                        <TableCell style={{ padding: '6px 10px' }} />
                                        {showLineTax && <TableCell style={{ padding: '6px 16px 6px 10px' }} />}
                                    </TableRow>
                                )}

                                {/* Labour row */}
                                {item?.pricingType === PRICING_TYPES.LABOUR && item?.labor && item?.labor?.qty > 0 ? (
                                    <TableRow>
                                        <TableCell style={{ fontSize: '16px', padding: '6px 0 6px 16px' }}>1</TableCell>
                                        <TableCell style={{ padding: '6px 10px 6px 0', fontSize: '16px' }}>
                                            <Typography variant="body2" sx={{ fontSize: '16px', fontWeight: 400, lineHeight: '143%' }}>
                                                Labour
                                            </Typography>
                                        </TableCell>
                                        <TableCell style={{ padding: '6px 10px', fontSize: '16px' }}>{item?.labor?.qty}</TableCell>
                                        <TableCell style={{ padding: '6px 10px', fontSize: '16px' }}>{currency(item?.labor?.ratePerHour)}</TableCell>
                                        <TableCell align={showLineTax ? 'left' : 'right'} style={{ padding: '6px 10px', fontSize: '16px', fontWeight: 400 }}>
                                            <Typography fontWeight={400} fontSize={16}>
                                                {currency(item?.labor?.totalAmount)}
                                            </Typography>
                                        </TableCell>
                                        {showLineTax && (
                                            <TableCell align="center" style={{ padding: '6px 16px 6px 10px', fontSize: '16px' }}>
                                                <LineTaxCell
                                                    taxesBreakdown={item?.labor?.taxesBreakdown}
                                                    lineAmount={item?.labor?.totalAmount}
                                                    documentType={DOCUMENT_TAX_TYPE.WORK_ORDER}
                                                    shopTaxesData={shopTaxesData}
                                                    provinceTaxGroup={provinceTaxGroup}
                                                    documentHasPartsOrPackages={documentHasPartsOrPackages}
                                                    kind={LINE_TAX_KIND.LABOUR}
                                                    editable={false}
                                                    discount={workOrderData?.discount}
                                                    subTotal={workOrderData?.subTotal}
                                                />
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ) : item?.pricingType === PRICING_TYPES.FXIED ? (
                                    /* Fixed price row */
                                    <TableRow>
                                        <TableCell style={{ fontSize: '16px', padding: '6px 0 6px 16px' }}>1</TableCell>
                                        <TableCell style={{ padding: '6px 10px 6px 0', fontSize: '16px' }}>
                                            <Typography variant="body2" sx={{ fontSize: '16px', fontWeight: 400, lineHeight: '143%' }}>
                                                Fixed Price
                                            </Typography>
                                        </TableCell>
                                        <TableCell style={{ padding: '6px 10px' }} />
                                        <TableCell style={{ padding: '6px 10px' }} />
                                        <TableCell align={showLineTax ? 'left' : 'right'} style={{ padding: '6px 10px', fontSize: '16px' }}>
                                            <Typography fontWeight={400} fontSize={16}>
                                                {currency(item?.fixedPrice)}
                                            </Typography>
                                        </TableCell>
                                        {showLineTax && (
                                            <TableCell align="center" style={{ padding: '6px 16px 6px 10px', fontSize: '16px' }}>
                                                <LineTaxCell
                                                    taxesBreakdown={item?.taxesBreakdown}
                                                    lineAmount={item?.fixedPrice}
                                                    documentType={DOCUMENT_TAX_TYPE.WORK_ORDER}
                                                    shopTaxesData={shopTaxesData}
                                                    kind={LINE_TAX_KIND.PACKAGE}
                                                    editable={false}
                                                    discount={workOrderData?.discount}
                                                    subTotal={workOrderData?.subTotal}
                                                />
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ) : null}

                                {/* Part rows */}
                                {item?.pricingType === PRICING_TYPES.LABOUR &&
                                    item?.parts?.map((row, index) => {
                                        const rowNumber = item?.labor?.qty > 0 ? index + 2 : index + 1;
                                        const amount = row?.totalAmount ?? NumericEngine.multiply(row?.totalQty || 0, row?.cost ?? row?.retailPrice ?? 0);
                                        // A managed part's per-unit fee is billed on its own row, exactly as on an invoice.
                                        const showFeeCell = row?.fee?.name && row.feeAmount > 0;

                                        return (
                                            <Fragment key={row?._id || index}>
                                                <TableRow>
                                                    <TableCell
                                                        style={{
                                                            fontSize: '16px',
                                                            padding: '6px 0 6px 16px',
                                                            borderBottom: showFeeCell || row?.lastDescription ? 'none' : '',
                                                        }}
                                                    >
                                                        {rowNumber}
                                                    </TableCell>

                                                    <TableCell
                                                        style={{
                                                            padding: '6px 10px 6px 0',
                                                            fontSize: '16px',
                                                            borderBottom: showFeeCell || row?.lastDescription ? 'none' : '',
                                                        }}
                                                    >
                                                        <Box>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '16px',
                                                                    fontWeight: 400,
                                                                    lineHeight: '143%',
                                                                    letterSpacing: '0.17px',
                                                                    textDecoration:
                                                                        row?.inventoryType === INVENTORY_MANAGE_TYPE.UNMANAGED ? 'none' : 'underline',
                                                                    textUnderlineOffset: '2px',
                                                                    cursor: row?.inventoryType === INVENTORY_MANAGE_TYPE.UNMANAGED ? 'default' : 'pointer',
                                                                }}
                                                                onClick={() => {
                                                                    if (row?.inventoryType === INVENTORY_MANAGE_TYPE.UNMANAGED) {
                                                                        return;
                                                                    }
                                                                    setSelectedInventoryId(row?.inventoryId || row?.inventory?._id);
                                                                    setInventoryOption(row);
                                                                    if (row?.inventoryBatches?.length > 0 && row?.inventoryBatches[0]?.batchNumber) {
                                                                        setBatchNumber(row?.inventoryBatches[0]?.batchNumber || row?.batchNumber?.[0]);
                                                                        setOpenpartModel(true);
                                                                    }
                                                                }}
                                                            >
                                                                {row?.partName || row?.inventory?.part?.partName}
                                                            </Typography>

                                                            <Stack
                                                                direction="row"
                                                                spacing={1.25}
                                                                divider={<Box sx={{ width: '1px', height: '16px', bgcolor: 'rgba(0, 0, 0, 0.12)' }} />}
                                                            >
                                                                {(row?.partNumber || row?.tyreDimension) && (
                                                                    <Stack direction="row" alignItems="center" spacing={0.5} mt={'4px'}>
                                                                        <Typography
                                                                            component="span"
                                                                            sx={{
                                                                                fontSize: '16px',
                                                                                fontWeight: 400,
                                                                                letterSpacing: '0.17px',
                                                                                color: '#006CDD',
                                                                            }}
                                                                        >
                                                                            #
                                                                        </Typography>
                                                                        <Typography
                                                                            component="span"
                                                                            sx={{
                                                                                fontSize: '16px',
                                                                                fontWeight: 400,
                                                                                letterSpacing: '0.17px',
                                                                                color: 'rgba(0, 0, 0, 0.87)',
                                                                            }}
                                                                        >
                                                                            {/* partsType is not always returned; fall back to whichever identifier the part carries. */}
                                                                            {(row?.part?.partsType ?? row?.partsType) === PART_TYPES.TYRE
                                                                                ? row?.tyreDimension || row?.partNumber
                                                                                : row?.partNumber || row?.tyreDimension}
                                                                        </Typography>
                                                                    </Stack>
                                                                )}

                                                                {row?.supplier?.name && (
                                                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                                                        <PartBrandSvg />
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: '16px',
                                                                                fontWeight: 400,
                                                                                letterSpacing: '0.17px',
                                                                                color: 'rgba(0, 0, 0, 0.87)',
                                                                            }}
                                                                        >
                                                                            {row.supplier.name}
                                                                        </Typography>
                                                                    </Stack>
                                                                )}

                                                                {row?.partsBrand?.name && (
                                                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                                                        <PartCategorySvg />
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: '16px',
                                                                                fontWeight: 400,
                                                                                letterSpacing: '0.17px',
                                                                                color: 'rgba(0, 0, 0, 0.87)',
                                                                            }}
                                                                        >
                                                                            {row.partsBrand.name}
                                                                        </Typography>
                                                                    </Stack>
                                                                )}
                                                            </Stack>
                                                        </Box>
                                                    </TableCell>

                                                    <TableCell
                                                        style={{
                                                            padding: '6px 10px',
                                                            fontSize: '16px',
                                                            borderBottom: showFeeCell || row?.lastDescription ? 'none' : '',
                                                        }}
                                                    >
                                                        {row?.totalQty}
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            padding: '6px 10px',
                                                            fontSize: '16px',
                                                            borderBottom: showFeeCell || row?.lastDescription ? 'none' : '',
                                                        }}
                                                    >
                                                        {currency(row?.cost ?? row?.retailPrice)}
                                                    </TableCell>
                                                    <TableCell
                                                        align={showLineTax ? 'left' : 'right'}
                                                        style={{
                                                            padding: '6px 10px',
                                                            fontSize: '16px',
                                                            borderBottom: showFeeCell || row?.lastDescription ? 'none' : '',
                                                        }}
                                                    >
                                                        <Typography fontWeight={400} fontSize={16}>
                                                            {currency(amount)}
                                                        </Typography>
                                                    </TableCell>

                                                    {showLineTax && (
                                                        <TableCell
                                                            align="center"
                                                            style={{
                                                                padding: '6px 16px 6px 10px',
                                                                fontSize: '16px',
                                                                borderBottom: showFeeCell || row?.lastDescription ? 'none' : '',
                                                            }}
                                                        >
                                                            <LineTaxCell
                                                                taxesBreakdown={row?.taxesBreakdown}
                                                                lineAmount={recordPartGrossAmount(row)}
                                                                documentType={DOCUMENT_TAX_TYPE.WORK_ORDER}
                                                                shopTaxesData={shopTaxesData}
                                                                kind={LINE_TAX_KIND.PARTS}
                                                                editable={false}
                                                                discount={workOrderData?.discount}
                                                                subTotal={workOrderData?.subTotal}
                                                            />
                                                        </TableCell>
                                                    )}
                                                </TableRow>

                                                {row?.lastDescription && (
                                                    <TableRow>
                                                        <TableCell style={{ padding: '0 12px 6px 16px', borderBottom: showFeeCell ? 'none' : undefined }} />
                                                        <TableCell
                                                            colSpan={3}
                                                            style={{ padding: '0 12px 6px 0', borderBottom: showFeeCell ? 'none' : undefined }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '14px',
                                                                    fontWeight: 400,
                                                                    lineHeight: '20px',
                                                                    letterSpacing: '0.4px',
                                                                    color: '#848484',
                                                                }}
                                                            >
                                                                {row?.lastDescription}
                                                            </Typography>
                                                        </TableCell>
                                                        {showLineTax && <TableCell style={{ paddingTop: 0, borderBottom: showFeeCell ? 'none' : undefined }} />}
                                                    </TableRow>
                                                )}

                                                {showFeeCell && (
                                                    <TableRow>
                                                        <TableCell style={{ padding: '0 12px 6px 16px', fontSize: '16px' }} />
                                                        <TableCell style={{ padding: '0 12px 6px 0', fontSize: '16px' }}>{row?.fee?.name}</TableCell>
                                                        <TableCell style={{ padding: '0 12px', fontSize: '16px' }}>{row?.totalQty}</TableCell>
                                                        <TableCell style={{ padding: '0 12px', fontSize: '16px' }}>{currency(row?.feeAmount)}</TableCell>
                                                        <TableCell align={showLineTax ? 'left' : 'right'} style={{ padding: '0 12px', fontSize: '16px' }}>
                                                            {currency(NumericEngine.multiply(row?.totalQty || 0, row?.feeAmount || 0))}
                                                        </TableCell>
                                                        {showLineTax && <TableCell style={{ padding: '0 16px 0 10px', fontSize: '16px' }} />}
                                                    </TableRow>
                                                )}
                                            </Fragment>
                                        );
                                    })}

                                {/* Per-service subtotal */}
                                {shouldShowPerServiceSubtotalRow(item, { variant: 'invoice-detail' }) && (
                                    <TableRow>
                                        <TableCell colSpan={4} style={{ background: '#fff', padding: '12px' }} />
                                        <TableCell
                                            align={showLineTax ? 'left' : 'right'}
                                            style={{ background: '#fff', padding: '6px 12px', fontSize: '16px', fontWeight: 400 }}
                                        >
                                            <Typography fontWeight={400} fontSize={16}>
                                                {Number(subtotal) > 0 ? currency(subtotal) : '$0.00'}
                                            </Typography>
                                        </TableCell>
                                        {showLineTax && <TableCell style={{ background: '#fff', padding: '6px 12px' }} />}
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            {/* Read-only inventory record for a managed part — same modal and props as the invoice. */}
            <AddInventoryModal
                type={INVENTORY_TYPES.INVOICE}
                showUsedInInvoiceFeild={true}
                FeildLabelName="Used in Work Order"
                isUsedInvoiceQty={inventoryOption?.totalQty}
                isUseLastCost={inventoryOption?.totalQty ? true : false}
                inventoryId={selectedInventoryId}
                batchNumber={batchNumber}
                viewOnly={true}
                openModal={openPartModel}
                closeModal={closePartModal}
                onSubmitModal={closePartModal}
                showLoader={false}
                setShowLoader={() => {}}
                selectedInventory={[]}
                setSelectedInventory={() => {}}
                fetchApi={() => {}}
                ShowAllTabs={false}
                ShowTabIndex={0}
                modalType={'edit'}
            />
        </>
    );
};

export default WorkOrderServiceTable;
